import { Landmark, University, Building, Scale, Check, ShieldCheck } from 'lucide-react';
import { Hero, SectionHeader, FeatureGrid, Slab, MockVisual, CTABanner } from '@/components/marketing/primitives.jsx';

const partnerTypes = [
  {
    icon: Landmark,
    title: 'Financial institutions',
    description:
      'Banks, issuers, and exchanges integrate with IFPS for real-time identity challenges and cross-institution pattern detection.',
  },
  {
    icon: University,
    title: 'Election authorities',
    description:
      'State and local election offices use VERIS for registration dedup, eligibility state management, and cross-jurisdiction dispute handling.',
  },
  {
    icon: Building,
    title: 'Federal program offices',
    description:
      'Treasury, HUD, and other federal program owners declare FFOs and drive gate evaluation on fund flows with reviewer-signed enforcement.',
  },
  {
    icon: Scale,
    title: 'Judicial reviewers',
    description:
      'Judges, magistrates, and prosecutors receive pre-packaged warrant requests with all evidence attached and signed for clarity.',
  },
];

const tiers = [
  {
    name: 'Observer',
    price: 'Sandbox-only',
    highlight: false,
    summary: 'Read-only access to the Interoperability Sandbox for evaluation and integration planning.',
    features: [
      'Sandbox endpoint access',
      'Synthetic data only',
      'Documentation + support forum',
      'No production credentials',
    ],
    cta: { to: '/contact', label: 'Request observer access' },
  },
  {
    name: 'Integration',
    price: 'Supervised',
    highlight: true,
    summary: 'Live integration against the sandbox with your real systems wired in. Technical onboarding support included.',
    features: [
      'Sandbox read/write access',
      'Dedicated technical onboarding',
      'Safe Entity Attestation preparation',
      'WebSocket channel provisioning',
    ],
    cta: { to: '/contact', label: 'Start integration' },
  },
  {
    name: 'Federated',
    price: 'Production',
    highlight: false,
    summary: 'Full production access with attested credentials, standing IG oversight, and signed integration charter.',
    features: [
      'Production API + real-time access',
      'Attested institution or agency credentials',
      'Inspector General oversight enabled',
      'Signed federated integration charter',
    ],
    cta: { to: '/contact', label: 'Sign the charter' },
  },
];

const guarantees = [
  {
    icon: ShieldCheck,
    title: 'Tenant isolation',
    description:
      'Your data — and only your data — is visible through your credentials. Cross-tenant access is structurally impossible, not policy-only.',
  },
  {
    icon: Check,
    title: 'Signed exchange',
    description:
      'Every inter-tenant exchange is signed and logged. You can verify the full chain of custody for any record you receive from NACIS.',
  },
];

export default function ForPartners() {
  return (
    <>
      <Hero
        eyebrow="For Partners"
        title="Integrate at the right tier — observer, integration, or federated."
        description="Banks, election authorities, program offices, and judicial reviewers onboard through a tiered path with Safe Entity Attestation and the Interoperability Sandbox at every step."
        primaryCta={{ to: '/contact', label: 'Start the conversation' }}
        secondaryCta={{ to: '/security', label: 'Security guarantees' }}
      />

      <section className="py-20 md:py-24">
        <div className="container-wide">
          <SectionHeader
            eyebrow="Who partners with NACIS"
            title="Four partner archetypes, one integrity contract."
          />
          <div className="mt-12">
            <FeatureGrid items={partnerTypes} columns={4} />
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-20 md:py-24">
        <div className="container-wide">
          <SectionHeader
            eyebrow="Partnership tiers"
            title="Start in sandbox. Graduate to production when you are ready."
          />
          <div className="mt-12 grid gap-6 lg:grid-cols-3">
            {tiers.map(t => (
              <div
                key={t.name}
                className={
                  'flex flex-col rounded-2xl border p-8 transition ' +
                  (t.highlight
                    ? 'border-slate-900 bg-slate-900 text-white shadow-lg'
                    : 'border-slate-200 bg-white text-slate-900')
                }
              >
                <div className="text-xs font-semibold uppercase tracking-[0.14em] opacity-70">{t.price}</div>
                <div className="mt-1 text-2xl font-semibold">{t.name}</div>
                <p className={'mt-4 text-sm leading-6 ' + (t.highlight ? 'text-slate-300' : 'text-slate-600')}>
                  {t.summary}
                </p>
                <ul className="mt-6 space-y-3 text-sm">
                  {t.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <Check className={'mt-0.5 h-4 w-4 flex-shrink-0 ' + (t.highlight ? 'text-sky-300' : 'text-sky-600')} />
                      <span className={t.highlight ? 'text-slate-200' : 'text-slate-700'}>{f}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-8">
                  <a
                    href={t.cta.to}
                    className={
                      'inline-flex w-full items-center justify-center rounded-md px-5 py-2.5 text-sm font-semibold transition ' +
                      (t.highlight
                        ? 'bg-white text-slate-900 hover:bg-slate-100'
                        : 'bg-slate-900 text-white hover:bg-slate-800')
                    }
                  >
                    {t.cta.label}
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Slab
        eyebrow="Safe Entity Attestation"
        title="A signed charter, not a click-through."
        description="Every federated partner signs an attestation covering authorised scope, credential handling, incident response, and data-retention obligations. This is the contract your staff and auditors can point to — not an EULA."
        bullets={[
          'Negotiated scope of API and channel access',
          'Explicit credential rotation policy',
          'Incident reporting obligations both ways',
          'Inspector General read access to your inter-NACIS logs',
        ]}
        image={<MockVisual accent="emerald" lines={5} />}
      />

      <section className="py-20 md:py-24">
        <div className="container-wide">
          <SectionHeader
            eyebrow="What you can count on"
            title="Guarantees we make to every federated partner."
          />
          <div className="mt-12">
            <FeatureGrid items={guarantees} columns={2} />
          </div>
        </div>
      </section>

      <CTABanner
        title="Let's discuss your integration."
        description="Tell us about your systems, your jurisdiction, and your constraints. We will map the right tier and path for you."
        primaryCta={{ to: '/contact', label: 'Contact partnerships' }}
      />
    </>
  );
}
